import { useState } from "react";
import { IconAlertTriangle } from "@tabler/icons-react";
import {
  Alert,
  Button,
  DatePickerInput,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Group,
  Stack,
  Text,
  Textarea,
} from "@/components/ui";
import { parseIso, formatIso } from "@/features/datos-generales/utils/iso-date";
import { useReprogramarFechaPago } from "../hooks/use-calendario-pagos";
import type { PagoProgramado } from "../services/calendario-pagos.api";

/**
 * Reprogramación de la fecha de pago (P39) · Mesa de Control, con motivo
 * obligatorio. Si la póliza ya se depositó se advierte que el TXT ya viajó
 * a SAP y el cambio solo actualiza el calendario del portal.
 */
export function ReprogramarFechaModal({
  pago,
  onClose,
}: {
  pago: PagoProgramado | null;
  onClose: () => void;
}) {
  const [fecha, setFecha] = useState<string>("");
  const [motivo, setMotivo] = useState("");
  const reprogramar = useReprogramarFechaPago();

  const cerrar = () => {
    setFecha("");
    setMotivo("");
    onClose();
  };

  const confirmar = () => {
    if (!pago || !fecha) return;
    reprogramar.mutate(
      { solicitudId: pago.solicitudId, nuevaFecha: fecha, motivo: motivo.trim() },
      { onSuccess: cerrar }
    );
  };

  const puedeConfirmar = fecha !== "" && fecha !== pago?.fechaPago && motivo.trim().length > 0;

  return (
    <Dialog open={pago !== null} onOpenChange={(open) => !open && cerrar()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reprogramar fecha de pago</DialogTitle>
        </DialogHeader>
        {pago && (
          <Stack gap="md">
            <Text size="sm">
              Solicitud <strong>{pago.folio}</strong> · {pago.proveedorRazonSocial ?? "—"} · fecha actual{" "}
              <strong>{pago.fechaPago}</strong>.
            </Text>

            {pago.depositado && (
              <Alert variant="warning" icon={<IconAlertTriangle size={18} />}>
                La póliza de esta solicitud YA se depositó ({pago.documentoContable ?? "TXT"}) · el archivo ya viajó
                a SAP. Reprogramar solo actualiza el calendario del portal, no revierte el depósito.
              </Alert>
            )}

            <DatePickerInput
              label="Nueva fecha de pago"
              placeholder="Selecciona la fecha…"
              value={parseIso(fecha)}
              onChange={(d) => setFecha(formatIso(d))}
              required
            />
            <Textarea
              label="Motivo de la reprogramación"
              placeholder="Obligatorio · quedará en la auditoría"
              value={motivo}
              onChange={(e) => setMotivo(e.currentTarget.value)}
              maxLength={500}
              rows={2}
              required
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={cerrar}>
                Cancelar
              </Button>
              <Button disabled={!puedeConfirmar} loading={reprogramar.isPending} onClick={confirmar}>
                Reprogramar
              </Button>
            </Group>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
